import { useEffect, useState } from "react";
import { Award, Mail } from "lucide-react";
import { toast } from "sonner";
import { getGolden, markGoldenSeen, assignGolden, getGoldenEmail, setGoldenEmail, SOURCE_LABEL } from "@/lib/golden";
import { errorText } from "@/lib/family";

const when = (iso) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

const WhoEarned = ({ g, pin, profiles, onDone }) => {
  if (g.source === "app") return <span className="pp-badge text-xs">{profiles.find((p) => p.id === g.profile_id)?.name || "Shared family"}</span>;
  const change = async (e) => {
    try { await assignGolden(pin, g.id, e.target.value); toast.success("Golden acorn updated"); onDone(); } catch (err) { toast.error(errorText(err)); }
  };
  return (
    <label className="pp-who">
      <span className="text-xs font-semibold">Who earned this?</span>
      <select className="pp-voice-select" value={g.profile_id || ""} onChange={change} data-testid={`golden-assign-${g.id}`}>
        <option value="">Shared family</option>
        {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    </label>
  );
};

const GoldenRow = ({ g, pin, profiles, reload }) => (
  <li className={`pp-golden-row ${g.seen ? "" : "fresh"}`} data-testid={`golden-row-${g.id}`}>
    <img src="/acorn-t.png" alt="" />
    <div className="min-w-0">
      <p className="font-bold flex flex-wrap items-center gap-2">
        {!g.seen && <span className="pp-new-badge" data-testid={`golden-new-${g.id}`}>New!</span>}
        {g.profile_id ? `${profiles.find((p) => p.id === g.profile_id)?.name || "Your child"} earned a Golden Acorn` : "Someone in your family earned a Golden Acorn"}
      </p>
      <p className="text-sm opacity-80">{g.skill} · {SOURCE_LABEL[g.source]} · {when(g.earned_at)} · +{g.quantity}</p>
      <p className="text-sm mt-1">{g.quote ? `“${g.quote}”` : g.category}</p>
    </div>
    <WhoEarned g={g} pin={pin} profiles={profiles} onDone={reload} />
  </li>
);

const EmailToggle = () => {
  const [on, setOn] = useState(null);
  useEffect(() => { getGoldenEmail().then(setOn); }, []);
  const flip = async () => { try { setOn(await setGoldenEmail(!on)); } catch (e) { toast.error(errorText(e)); } };
  if (on === null) return null;
  return (
    <label className="pp-toggle mt-3" data-testid="golden-email-toggle">
      <input type="checkbox" checked={on} onChange={flip} data-testid="golden-email-checkbox" />
      <Mail className="h-4 w-4" /> Email me when a Golden Acorn is earned
    </label>
  );
};

export const GoldenCollection = ({ pin, profiles }) => {
  const [data, setData] = useState(null);
  const reload = () => getGolden().then(setData).catch((e) => toast.error(errorText(e)));
  useEffect(() => {
    getGolden().then((d) => { setData(d); if (d.new) markGoldenSeen(pin).catch(() => {}); }).catch((e) => toast.error(errorText(e)));
  }, [pin]);
  return (
    <section className="pp-card" id="golden" data-testid="golden-collection">
      <p className="pp-label">Parents only</p>
      <h2 className="pp-h2 flex items-center gap-2"><Award className="h-5 w-5 text-amber-500" /> Golden Acorn Collection</h2>
      <p className="opacity-75 mt-1">Moments when your child went beyond “thank you” and said what they appreciated, in the NICE app or on Siri.</p>
      <EmailToggle />
      {data && data.items.length === 0 && <p className="pp-sub mt-4" data-testid="golden-empty">No Golden Acorns yet. They'll appear here when thoughtful gratitude is shared.</p>}
      <ul className="mt-4 grid gap-2" data-testid="golden-list">
        {data?.items.map((g) => <GoldenRow key={g.id} g={g} pin={pin} profiles={profiles} reload={reload} />)}
      </ul>
    </section>
  );
};
